import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Moon, Sun, Waves, Sparkles, Check } from "lucide-react";

const ThemeContext = createContext(null);

function getCookie(name) {
  const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[2]) : null;
}

function setCookie(name, value, days = 365) {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${days * 86400}; samesite=lax`;
}

export { getCookie, setCookie };

export const THEMES = [
  { id: "fluid", label: "Fluid Dark", hint: "Animated deep-blue WebGL liquid", Icon: Waves },
  { id: "aurora", label: "Aurora Dark", hint: "Navy with glowing purple-blue orbs", Icon: Sparkles },
  { id: "light", label: "Light", hint: "Warm off-white and silver", Icon: Sun },
];

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const saved = getCookie("dt-theme");
    return ["fluid", "aurora", "light"].includes(saved) ? saved : "fluid";
  });

  useEffect(() => {
    const dark = theme === "light" ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", dark);
    document.documentElement.setAttribute("data-dark-variant", theme === "light" ? "" : theme);
    setCookie("dt-theme", theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle: () => setTheme((t) => (t === "light" ? "fluid" : "light")) }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

/** Global theme switcher — Fluid Dark / Aurora Dark / Light. Preference saved in cookies. */
export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, []);

  const Current = THEMES.find((t) => t.id === theme) || THEMES[0];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        aria-label="Switch theme"
        className="w-9 h-9 rounded-full border border-dt-border bg-dt-card flex items-center justify-center text-dt-muted hover:text-dt-text hover:border-dt-accent transition-all duration-200 hover:scale-110"
        data-testid="theme-toggle"
      >
        <Current.Icon className="w-4 h-4" />
      </button>
      {open && (
        <div className="dropdown-ease absolute right-0 top-11 w-60 card-dt glass-panel p-2 z-50" role="menu" data-testid="theme-menu">
          <p className="text-[10px] uppercase tracking-[0.18em] text-dt-muted font-bold px-3 pt-2 pb-1.5">Theme</p>
          {THEMES.map((t) => (
            <button key={t.id} role="menuitem" onClick={() => { setTheme(t.id); setOpen(false); }}
              className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-dt-accent/10 transition-colors text-left"
              data-testid={`theme-option-${t.id}`}>
              <span className="w-8 h-8 rounded-lg border border-dt-border bg-dt-bg2 flex items-center justify-center text-dt-muted">
                <t.Icon className="w-4 h-4" />
              </span>
              <span className="flex-1">
                <span className="block text-sm font-semibold text-dt-text">{t.label}</span>
                <span className="block text-[11px] text-dt-muted">{t.hint}</span>
              </span>
              {theme === t.id && <Check className="w-4 h-4 text-dt-accent" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
